// Report controller — extracted verbatim from ResultScene.ts (FACE&FURY S2 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were
// rewritten to `export function name(this: ResultScene, ...)` and bound back onto the scene as fields.
import { PALETTE, hex, FONT } from '../keys';
import { typePx } from '../ui/typeScale';
import { textScale } from '../settings';
import { advanceSeries, opponentSeries, startSeries } from '../series';
import { button, modal } from '../ui';
import { secondaryButton } from '../ui/kit';
import type { IconName } from '../icons';
import type { RunBoard } from '../run';
import { shareResult } from '../share';
import { getMintedGhostId } from '../mintedGhost';
import { submitBoardReport } from '../../net/report';
import type { ReportReason, SubmitReportInput } from '../../net/types';
import { track } from '../../analytics/client';
import { copyFeelReviewDraftCsv, type FeelReviewDraft } from '../playtestSession';
import type { ResultScene } from '../scenes/ResultScene';


export function onShare(this: ResultScene, board: RunBoard, peakCombo: number, won: boolean): void {
    const snap = this.args.snapshot;
    track('challenge_opened', this.analyticsDims());
    track('share_opened', this.analyticsDims());
    // Spoiler-free Wordle-style route grid: pass the final cells + dims straight through. The human
    // beat the rival iff the rival lost the board to player 0 (AI matches only).
    const beatRival = this.args.ai ? snap.winner === 0 : false;
    // P1.2c best-of-three: a CHALLENGE always sends a best-of-three. Start one if this isn't already a
    // series (so the feature is reachable — every challenge is game 1). If we ARE in a series and this
    // was a RESOLVE game (a target was carried), advance the standings with THIS game's result BEFORE
    // encoding — otherwise re-sharing a resolved game would silently re-send it un-advanced. Then flip
    // perspective (their "you" is our "them") so the friend continues from here.
    const human = snap.players.find((p) => p.id === 0);
    const carriedTarget = Math.max(0, Math.floor(this.args.challengeTarget ?? 0));
    let outSeries = this.args.series ?? startSeries();
    if (this.args.series && carriedTarget > 0) {
      outSeries = advanceSeries(this.args.series, (human?.score ?? 0) >= carriedTarget);
    }
    const op = opponentSeries(outSeries);
    // GHOST DUELS Slice B: if THIS daily run was uploaded as a raceable ghost at the score submit, mint its
    // KV id into the link (&gr=<id>) so the recipient races the live replay. '' when not uploaded / not the
    // matching run — the share then carries only the static-footprint fallback exactly as before.
    const today = new Date().toISOString().slice(0, 10);
    const ghostRemoteId = this.args.isDaily ? getMintedGhostId(today, this.args.seed, this.args.topic) : '';
    void shareResult({
      topicLabel: board.topicLabel,
      rule: snap.rule,
      score: board.humanScore,
      bestCombo: peakCombo,
      eatenCorrect: board.eatenCorrect,
      quota: board.quota,
      won,
      seed: this.args.seed,
      topicNormalized: this.args.topic,
      isDaily: !!this.args.isDaily,
      dailyMutatorId: this.args.dailyMutatorId,
      boardIndex: this.args.boardIndex,
      cells:
        this.shareRouteCells ??
        snap.cells.map((c) => ({
          kind: c.kind,
          eaten: c.eaten,
          eatenBy: c.eatenBy,
          armed: c.armed,
        })),
      rows: snap.rows,
      cols: snap.cols,
      hasRival: this.args.ai,
      beatRival,
      seriesMode: op?.mode,
      seriesGame: op?.game,
      seriesYou: op?.youWins,
      seriesThem: op?.themWins,
      ghostRemoteId,
      // FACE & FURY S5: badge the share card + brag line when this board was a PERFECT GAUNTLET.
      perfectGauntlet: this.args.perfectGauntlet,
      // S9 FIX-D: the REAL gauntlet-event thread tally (RunBoard.threads = GameScene's threadsThisBoard)
      // so the exported card/text legend matches the Result's THREADED stat byte-for-byte with the
      // sheet preview (the snapshot's armed cells expire in 0.6s and under-report).
      threads: board.threads,
    }).then((outcome) => {
      const msg =
        outcome.method === 'shared'
          ? 'Shared!'
          : outcome.method === 'copied'
          ? 'Link copied!'
          : outcome.method === 'downloaded'
          ? 'Card saved!'
          : 'Could not share — try again.';
      if (outcome.method !== 'failed') {
        track('challenge_completed', this.analyticsDims({ result: outcome.method }));
        track('share_completed', this.analyticsDims({ result: outcome.method }));
      }
      this.toast(msg, outcome.method !== 'failed');
    });
  }


export function openFeelReviewDraftModal(this: ResultScene, draft: FeelReviewDraft): void {
    this.modalOpen = true;
    const m = modal(this, {
      title: 'PLAYTEST ROW',
      widthFrac: 0.94,
      heightFrac: 0.58,
      onClose: () => {
        this.modalOpen = false;
      },
    });
    const { card, cardW, cardH } = m;
    const note = this.add
      .text(0, -cardH / 2 + 64, 'Draft only. Observer verifies blanks before appending to sessions.csv.', {
        fontFamily: FONT.family,
        fontSize: `${typePx(this, 'caption', { vw: 0.021 })}px`,
        color: hex(PALETTE.textDim),
        align: 'center',
        wordWrap: { width: cardW - 40 },
      })
      .setOrigin(0.5);
    const csv = this.add
      .text(0, -cardH / 2 + 146, draft.csv, {
        fontFamily: 'monospace',
        fontSize: `${typePx(this, 'caption', { vw: 0.019 })}px`,
        color: hex(PALETTE.text),
        align: 'left',
        wordWrap: { width: cardW - 42, useAdvancedWrap: true },
      })
      .setOrigin(0.5);
    const status = this.add
      .text(0, cardH / 2 - 24, '', {
        fontFamily: FONT.family,
        fontSize: `${typePx(this, 'caption', { vw: 0.02 })}px`,
        color: hex(PALETTE.textDim),
        align: 'center',
        wordWrap: { width: cardW - 40 },
      })
      .setOrigin(0.5);
    const copy = button(
      this,
      0,
      cardH / 2 - 70,
      'COPY ROW',
      () => {
        void copyFeelReviewDraftCsv(draft).then((ok) => {
          status.setText(ok ? 'Copied. Paste into a draft, then verify human fields.' : 'Clipboard blocked. Row is exposed on window.__feelReviewDraftCsv.');
        });
      },
      {
        fill: PALETTE.scan,
        textColor: PALETTE.bg,
        stroke: PALETTE.scan,
        width: cardW - 44,
        height: 36,
        fontScale: 0.76,
      },
    );
    card.add([note, csv, status, copy]);
  }


export function openReportModal(this: ResultScene, topicLabel: string): void {
    this.modalOpen = true;
    // Each reason carries a leading icon for scannability; ALL options share ONE neutral text color so
    // the list reads as a calm menu, not four differently-colored buttons (S3 item 19).
    const reasons: Array<{ label: string; reason: ReportReason; icon: IconName }> = [
      { label: 'WRONG ANSWER', reason: 'wrong_answer', icon: 'xCircle' },
      { label: 'UNSAFE', reason: 'unsafe', icon: 'shield' },
      { label: 'OUTDATED', reason: 'outdated', icon: 'clock' },
      { label: 'OTHER', reason: 'other', icon: 'info' },
    ];

    // S3b gap 5 — the hug height must ACCOUNT FOR the accessibility text-scale: intro copy + status
    // line grow with textScale() (they can wrap to more lines at 150%), so their bands scale too; the
    // button rows grow modestly so a scaled label still breathes. Without this the card clipped its
    // last reason / status at the largest text step.
    const ts = textScale();
    const introH = Math.round(44 * ts);
    const rowH = Math.round(46 + 10 * (ts - 1));
    const rowGap = 10;
    const statusH = Math.round(30 * ts);
    // Content-hug: intro + N rows + status. modal() sizes the card to exactly fit (no dead band).
    const contentHeight = introH + reasons.length * rowH + (reasons.length - 1) * rowGap + statusH;
    const m = modal(this, {
      title: 'REPORT BOARD',
      contentHeight,
      // Rail calmed to the result palette (muted grey) — the bright scan rail read as an alert on a
      // dark result screen; a report menu should feel quiet, not urgent.
      railGrad: [PALETTE.dim],
      onClose: () => {
        this.modalOpen = false;
      },
    });
    const { card, cardW, cardH } = m;

    const intro = this.add
      .text(0, -cardH / 2 + 58, 'Help us keep boards trustworthy — what looks wrong?', {
        fontFamily: FONT.family,
        fontSize: `${typePx(this, 'caption', { vw: 0.022, max: 17 })}px`,
        color: hex(PALETTE.textDim),
        align: 'center',
        wordWrap: { width: cardW - 40 },
      })
      .setOrigin(0.5);
    card.add(intro);

    const btnW = cardW - 44;
    const startY = -cardH / 2 + introH + 50;

    // #50 — the status line seats directly UNDER the last reason row (top-anchored), then relayout()
    // pulls the card bottom up to hug it. The old status was bottom-pinned (cardH/2-18), which left a
    // dead band between OTHER and the card edge because contentHeight over-reserved the bottom.
    const lastRowY = startY + (reasons.length - 1) * (rowH + rowGap);
    const statusY = lastRowY + rowH / 2 + Math.round(20 * ts);
    const status = this.add
      .text(0, statusY, '', {
        fontFamily: FONT.family,
        fontSize: `${typePx(this, 'caption', { vw: 0.02 })}px`,
        color: hex(PALETTE.textDim),
        align: 'center',
        wordWrap: { width: cardW - 40 },
      })
      .setOrigin(0.5);
    card.add(status);

    reasons.forEach((entry, index) => {
      const b = secondaryButton(
        this,
        0,
        startY + index * (rowH + rowGap),
        entry.label,
        () => {
          if (this.reportPending) return;
          this.reportPending = true;
          status.setText('Sending…');
          void submitBoardReport(this.reportPayload(entry.reason, topicLabel), { timeoutMs: 4_000 }).then((outcome) => {
            this.reportPending = false;
            m.close();
            this.toast(outcome.live ? 'Report sent. Thanks.' : 'Could not send report.', outcome.live);
          });
        },
        { width: btnW, height: rowH, icon: entry.icon, fontPx: 15 },
      );
      card.add(b);
    });

    // #50 hug: resize the card to end just below the status line (top edge stays fixed, card re-centres).
    m.relayout(statusY + Math.round(12 * ts) - m.bodyTop);
  }


export function reportPayload(this: ResultScene, reason: ReportReason, topicLabel: string): SubmitReportInput {
    return {
      topic: topicLabel,
      topicNormalized: this.args.topic,
      rule: this.args.snapshot.rule,
      reason,
      seed: this.args.seed,
      boardIndex: this.args.boardIndex,
      cells: this.args.snapshot.cells.map((cell) => ({
        id: cell.id,
        text: cell.text,
        displayText: cell.displayText !== cell.text ? cell.displayText : undefined,
        kind: cell.kind,
      })),
    };
  }


  /**
   * A brief bottom toast for share/copy/report confirmations. Keeps the button family (S3 item 21):
   * UPPERCASE display face + a leading ✓ (success) / ✗ (failure) glyph, same casing as the buttons
   * that trigger it, on the correct/hazard fill. `ok=false` marks a failure (hazard fill + ✗).
   */
export function toast(this: ResultScene, msg: string, ok = true): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const t = this.add
      .text(W / 2, H * 0.95, `${ok ? '✓' : '✗'}  ${msg.toUpperCase()}`, {
        fontFamily: FONT.display,
        fontSize: `${typePx(this, 'body', { vw: 0.026, min: 13 })}px`,
        color: hex(PALETTE.ink),
        backgroundColor: hex(ok ? PALETTE.correct : PALETTE.hazard),
        padding: { x: 14, y: 8 },
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(40_000)
      .setAlpha(0);
    this.tweens.add({ targets: t, alpha: 1, duration: 140, yoyo: true, hold: 1100, onComplete: () => t.destroy() });
  }
